import { motion } from "motion/react";
import { 
  Cpu, Code, Smartphone, Layout, Cloud, ShieldCheck, 
  BarChart3, ShoppingCart, Server, Boxes, CheckCircle2, Globe 
} from "lucide-react";
import { Link } from "react-router";
import { useState } from "react";
import { ProjectModal } from "./ProjectModal";

const services = [
  {
    icon: <Cpu size={32} />,
    title: "Organizational AI Transformation",
    description: "Leverage Large Language Models (LLMs) and predictive analytics to automate complex workflows, enhance decision-making, and unlock new revenue streams.",
    features: ["Custom AI Model Integration", "Workflow Automation", "Predictive Analytics", "Chatbot Development"]
  },
  {
    icon: <Code size={32} />,
    title: "Custom Software Development",
    description: "Tailor-made software solutions designed to address your specific business challenges. We build scalable, secure, and high-performance applications.",
    features: ["Enterprise Software", "SaaS Development", "API Integration", "Legacy Modernization"]
  },
  {
    icon: <Smartphone size={32} />,
    title: "Mobile App Development",
    description: "Native and cross-platform mobile applications that provide seamless user experiences on iOS and Android devices.",
    features: ["iOS & Android", "React Native / Flutter", "App Store Optimization", "Mobile UI/UX"]
  },
  {
    icon: <Layout size={32} />,
    title: "UX/UI Strategy & Design",
    description: "User-centric design strategies that combine aesthetics with usability. We conduct deep research to build intuitive interfaces that convert.",
    features: ["User Research", "Wireframing & Prototyping", "Design Systems", "Usability Testing"]
  },
  {
    icon: <Cloud size={32} />,
    title: "Cloud Infrastructure & DevOps",
    description: "Scalable cloud architectures and CI/CD pipelines to ensure your applications are always available, secure, and easy to deploy.",
    features: ["AWS / Azure / GCP", "Docker & Kubernetes", "Infrastructure as Code", "Cost Optimization"]
  },
  {
    icon: <ShieldCheck size={32} />,
    title: "Cyber Security & Compliance",
    description: "Comprehensive security assessments and implementation to protect your sensitive data and ensure compliance with global standards.",
    features: ["Penetration Testing", "Security Audits", "GDPR/HIPAA Compliance", "Data Encryption"]
  },
  {
    icon: <BarChart3 size={32} />,
    title: "Data Analytics & BI",
    description: "Turn your raw data into actionable insights. We build custom dashboards and data warehouses to help you make informed decisions.",
    features: ["Data Warehousing", "Business Intelligence", "Visualization Dashboards", "Big Data Processing"]
  },
  {
    icon: <ShoppingCart size={32} />,
    title: "E-Commerce Solutions",
    description: "Robust online stores built on Shopify, Magento, or custom headless architectures to drive sales and improve customer retention.",
    features: ["Custom Storefronts", "Payment Gateway Integration", "Inventory Management", "Headless Commerce"]
  },
  {
    icon: <Server size={32} />,
    title: "Legacy System Modernization",
    description: "Revitalize your outdated software. We refactor and migrate legacy systems to modern technologies without disrupting your business.",
    features: ["Code Refactoring", "Cloud Migration", "Database Optimization", "Tech Stack Upgrades"]
  },
  {
    icon: <Boxes size={32} />,
    title: "Blockchain & Web3",
    description: "Explore the potential of decentralized technologies. We build smart contracts, DApps, and blockchain solutions for transparent operations.",
    features: ["Smart Contracts", "DApp Development", "Tokenization", "Private Blockchains"]
  },
  {
    icon: <CheckCircle2 size={32} />,
    title: "Quality Assurance & Testing",
    description: "Ensure your software is bug-free and reliable. We provide automated and manual testing services to guarantee high quality.",
    features: ["Automated Testing", "Performance Testing", "Security Testing", "UAT Support"]
  },
  {
    icon: <Globe size={32} />,
    title: "IT Strategy Consulting",
    description: "Align your technology with your business goals. Our experts help you navigate the digital landscape and plan for long-term growth.",
    features: ["Digital Transformation", "CTO Advisory", "Tech Roadmap", "Vendor Management"]
  }
];

export function ServicesPage() {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <div className="pt-20">
      {/* Hero Header */}
      <section className="bg-slate-900 text-white py-24 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-[50%] h-[50%] bg-[#FD5750] rounded-full blur-[120px]" />
          <div className="absolute bottom-0 left-0 w-[30%] h-[30%] bg-blue-500 rounded-full blur-[100px]" />
        </div>
        
        <div className="container mx-auto px-6 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-[#FD5750] font-bold tracking-widest uppercase mb-4 block">Our Expertise</span>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Comprehensive <span className="text-[#FD5750]">Digital Solutions</span>
            </h1>
            <p className="text-slate-300 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed">
              From strategic consulting to full-scale development, we provide the technical firepower you need to innovate and grow.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl border border-transparent hover:border-[#FD5750]/20 transition-all duration-300 group flex flex-col h-full"
              >
                <div className="w-16 h-16 bg-slate-100 rounded-xl flex items-center justify-center text-slate-700 mb-6 group-hover:bg-[#FD5750] group-hover:text-white transition-colors duration-300">
                  {service.icon}
                </div>
                
                <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-[#FD5750] transition-colors">
                  {service.title}
                </h3>
                
                <p className="text-slate-600 mb-6 flex-grow leading-relaxed">
                  {service.description}
                </p>

                <div className="pt-6 border-t border-slate-100">
                  <ul className="grid grid-cols-1 gap-2">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-slate-500">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#FD5750]"></span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-white border-t border-slate-100">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
            Not sure what you need?
          </h2>
          <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">
            Let's have a conversation. We'll help you diagnose your challenges and recommend the right technical solution.
          </p>
          <button
            onClick={() => setIsModalOpen(true)}
            className="inline-flex items-center justify-center px-8 py-4 bg-slate-900 text-white font-bold rounded-lg hover:bg-[#FD5750] transition-colors shadow-lg cursor-pointer"
          >
            Book a Free Consultation
          </button>
        </div>
      </section>

      <ProjectModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
}
